import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PageLayout from '../pagelayout';
import { useIntl } from 'react-intl';
import translations from './practicetype.uha.i18n';
import * as css from '../components/tabs/tabs.module.scss';
import Icon, { IconType } from '../components/icon';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import {
  Route,
  Routes,
  useNavigate,
  Outlet,
  useParams,
} from 'react-router-dom';
import Button from '../components/button';
import { BackButton } from '../pagelayout/backButton';
import Modal from '../components/modal';
import PanelHeader from '../components/panelheader/panelHeader';
import Spinner from '../components/spinner/spinner';
import PraksisstederWithAvtale from './practiceplaceswithagreement.uha';
import {
  HttpError,
  PraksisstederForAktivitetskravDto,
  PraksistypeMedAktivitetskravDto,
} from '../api/generated/studiested-generated-api';
import { getLeafNodes } from '../api/praksisstedNodes';
import { ConfirmationDialog } from '../components/dialog';
import { TextInput } from '../components/input';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import { EmptyState } from '../components/emptyState/emptyState';
import { Tab, Tabs } from '../components/tabs/tabs';
import { PageNotFoundWithoutLayout } from '../pages/pagenotfound';

const PracticeTypePage = () => {
  const { kode } = useParams<{ kode: string }>();
  const navigate = useNavigate();
  const { praksistypeApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);

  const {
    data: praksistypeInfo,
    loading,
    reload,
  } = useLoader(
    () =>
      praksistypeApi.praksistypeKodeGet({ pathParams: { kode: kode || '' } }),
    [kode],
    true,
    true,
  );

  const { formatMessage } = useIntl();
  const labels = {
    subtitle: formatMessage({ id: translations.subtitle.id }),
    panelTitle: formatMessage({ id: translations.panelTitle.id }),
    back: formatMessage({ id: translations.back.id }),
    newRequirement: formatMessage({ id: translations.newRequirement.id }),
    newRequirementModalTitle: formatMessage({
      id: translations.newRequirementModalTitle.id,
    }),
    newRequirementModalConfirm: formatMessage({
      id: translations.newRequirementModalConfirm.id,
    }),
    successNewRequirement: formatMessage({
      id: translations.successNewRequirement.id,
    }),
    noRequirementChosenLabel: formatMessage({
      id: translations.noRequirementChosenLabel.id,
    }),
    noRequirementChosenText: formatMessage({
      id: translations.noRequirementChosenText.id,
    }),
  };

  const saveNewRequirement = (requirementName: string, close: () => void) => {
    praksistypeApi
      .praksistypeKodePost({
        pathParams: { kode: kode || '' },
        body: requirementName,
      })
      .then((requirementId) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.successNewRequirement,
        });
        close();
        reload();
        navigate(requirementId);
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const { open: newRequirement, Modal: newRequirementModal } =
    useRequirementModal(
      labels.newRequirementModalTitle,
      labels.newRequirementModalConfirm,
      saveNewRequirement,
    );

  const defaultActionButtons = (
    <>
      <BackButton
        title={labels.back}
        path={'/myinstitution/practicetypes/withrequirements'}
      />
      <Button.Primary onClick={newRequirement}>
        {labels.newRequirement}
      </Button.Primary>
    </>
  );

  const [actionButtons, setActionButtons] =
    useState<ReactNode>(defaultActionButtons);

  const pageTitle = praksistypeInfo?.praksistype.navn
    ? praksistypeInfo.praksistype.navn
    : labels.panelTitle;

  return (
    <PageLayout
      documentTitle={pageTitle}
      title={praksistypeInfo?.praksistype.navn || ''}
      subtitle={labels.subtitle}
      actionItems={actionButtons}
      isIndented
    >
      {newRequirementModal}
      {!loading && praksistypeInfo ? (
        <Routes>
          <Route
            path="/"
            element={
              <>
                <PanelHeader title={labels.panelTitle} />
                <Tabs>
                  {praksistypeInfo.aktivitetskrav.map((krav) => (
                    <Tab key={krav.id} id={krav.id} to={`${krav.id}`}>
                      {krav.navn}
                    </Tab>
                  ))}
                  <div
                    tabIndex={0}
                    id={'new-requirement-button'}
                    className={css.tab}
                    onClick={newRequirement}
                    onKeyPress={newRequirement}
                  >
                    <Icon type={IconType.AddCircleRounded} />
                    &nbsp;
                    <span>{labels.newRequirement}</span>
                  </div>
                </Tabs>
                <Outlet />
              </>
            }
          >
            <Route
              path=":id"
              element={
                <PracticeType
                  praksistypeInfo={praksistypeInfo}
                  setActionButtons={setActionButtons}
                  reloadPracticeType={reload}
                />
              }
            />
            <Route
              path=""
              element={
                <EmptyState
                  title={labels.noRequirementChosenLabel}
                  text={labels.noRequirementChosenText}
                />
              }
            />
            <Route path="*" element={<PageNotFoundWithoutLayout />} />
          </Route>
        </Routes>
      ) : (
        <Spinner />
      )}
    </PageLayout>
  );
};

interface PracticeTypeProps {
  praksistypeInfo: PraksistypeMedAktivitetskravDto;
  setActionButtons: (buttons: ReactNode) => void;
  reloadPracticeType: () => void;
}

const PracticeType: React.FC<React.PropsWithChildren<PracticeTypeProps>> = ({
  praksistypeInfo,
  setActionButtons,
  reloadPracticeType,
}) => {
  const { id: aktivitetskravId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { praksistypeApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);

  const { data: praksissteder, loading } = useLoader(
    (): Promise<Array<PraksisstederForAktivitetskravDto>> =>
      praksistypeApi.aktivitetskravIdStederGet({
        pathParams: { id: aktivitetskravId || '' },
      }),
    [aktivitetskravId],
    true,
    true,
  );

  const { formatMessage } = useIntl();
  const labels = {
    back: formatMessage({ id: translations.back.id }),
    delete: formatMessage({ id: translations.delete.id }),
    saveRequirements: formatMessage({ id: translations.saveRequirements.id }),
    deleteDialogTitle: formatMessage({ id: translations.deleteDialogTitle.id }),
    deleteDialogContent: formatMessage({
      id: translations.deleteDialogContent.id,
    }),
    newRequirement: formatMessage({ id: translations.newRequirement.id }),
    editRequirementName: formatMessage({
      id: translations.editRequirementName.id,
    }),
    successRequirementsSaved: formatMessage({
      id: translations.successRequirementsSaved.id,
    }),
    successDelete: formatMessage({ id: translations.successDelete.id }),
    successNewName: formatMessage({ id: translations.successNewName.id }),
    newRequirementNameModalTitle: formatMessage({
      id: translations.newRequirementNameModalTitle.id,
    }),
    newRequirementNameModalConfirm: formatMessage({
      id: translations.newRequirementNameModalConfirm.id,
    }),
  };

  const saveEditedRequirement = (
    requirementName: string,
    close: () => void,
  ) => {
    praksistypeApi
      .aktivitetskravIdNavnPut({
        pathParams: { id: aktivitetskravId || '' },
        body: requirementName,
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.successNewName,
        });
        close();
        reloadPracticeType();
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const selectedAktivitetskrav = useMemo(
    () =>
      praksistypeInfo.aktivitetskrav.find(
        (krav) => krav.id === aktivitetskravId,
      ),
    [praksistypeInfo, aktivitetskravId],
  );

  const { open: editRequirement, Modal: editRequirementModal } =
    useRequirementModal(
      labels.newRequirementNameModalTitle,
      labels.newRequirementNameModalConfirm,
      saveEditedRequirement,
      selectedAktivitetskrav && selectedAktivitetskrav.navn,
    );

  const handleDeleteConfirmationClose = (confirmed: boolean) => {
    setShowDeleteConfirmation(false);
    if (confirmed && aktivitetskravId) {
      praksistypeApi
        .aktivitetskravIdDelete({ pathParams: { id: aktivitetskravId } })
        .then(() => {
          addNotification({
            id: uuidv4(),
            type: AlpNotificationType.SUCCESS,
            message: labels.successDelete,
          });
          reloadPracticeType();

          if (praksistypeInfo.aktivitetskrav.length > 0) {
            navigate(`../${praksistypeInfo.aktivitetskrav[0].id}`);
          }
        })
        .catch((err: HttpError) => {
          addNotification({
            id: uuidv4(),
            type: AlpNotificationType.ERROR,
            message: err.body,
          });
        });
    }
  };

  const getPraksisstedIdsWithKravOppfylt = (
    steder: PraksisstederForAktivitetskravDto[],
  ) =>
    getLeafNodes(steder).reduce(
      (map, node) => map.set(node.id, node.oppfyllerKrav),
      new Map(),
    );

  const [
    initialPraksisstedIdsWithKravOppfylt,
    setInitialPraksisstedIdsWithKravOppfylt,
  ] = useState<Map<string, boolean>>();

  const [praksisstedIdsWithKravOppfylt, setPraksisstedIdsWithKravOppfylt] =
    useState<Map<string, boolean>>(new Map());

  const defaultActionButtons = useMemo(
    () => (
      <>
        <BackButton
          title={labels.back}
          path={'/myinstitution/practicetypes/withrequirements'}
        />
        <Button.Primary
          onClick={() => {}}
          disabled={true}
          id="saveRequirementsButton"
        >
          {labels.saveRequirements}
        </Button.Primary>
      </>
    ),
    [labels.back, labels.saveRequirements],
  );

  useEffect(() => {
    if (praksissteder) {
      const initialSteder = getPraksisstedIdsWithKravOppfylt(praksissteder);

      setInitialPraksisstedIdsWithKravOppfylt(initialSteder);
      setPraksisstedIdsWithKravOppfylt(initialSteder);
      setActionButtons(defaultActionButtons);
    }
  }, [aktivitetskravId, praksissteder, setActionButtons, defaultActionButtons]);

  const getActionButtons = (changed: boolean, steder: Map<string, boolean>) => (
    <>
      <BackButton
        title={labels.back}
        path={'/myinstitution/practicetypes/withrequirements'}
      />
      <Button.Primary
        onClick={() => saveStederWithKravOppfylt(steder)}
        disabled={!changed}
        id="saveRequirementsButton"
      >
        {labels.saveRequirements}
      </Button.Primary>
    </>
  );

  const countPraksisstederWithKravOppfylt = (
    stedIdsWithKravOppfylt: Map<string, boolean>,
  ): number => {
    return Array.from(stedIdsWithKravOppfylt.entries()).filter(
      ([, oppfyllerKrav]) => oppfyllerKrav,
    ).length;
  };

  const updatePraksisstedIdsWithKravOppfylt = (
    steder: Map<string, boolean>,
  ): number => {
    const currentIds = new Map(praksisstedIdsWithKravOppfylt.entries());
    if (currentIds) {
      Array.from(steder.entries()).forEach(([key, value]) =>
        currentIds.set(key, value),
      );

      const changed = Array.from(currentIds).some(
        ([key, value]) =>
          initialPraksisstedIdsWithKravOppfylt!.get(key) !== value,
      );

      setPraksisstedIdsWithKravOppfylt(currentIds);
      setActionButtons(getActionButtons(changed, currentIds));
      return countPraksisstederWithKravOppfylt(currentIds);
    }

    return 0;
  };

  const saveStederWithKravOppfylt = (steder: Map<string, boolean>) => {
    if (!aktivitetskravId) return;

    const leafNodeIds: string[] = Array.from(steder.entries())
      .filter(([, oppfyllerKrav]) => oppfyllerKrav)
      .map(([id]) => id);

    praksistypeApi
      .aktivitetskravIdStederPut({
        pathParams: { id: aktivitetskravId },
        requestBody: leafNodeIds,
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.successRequirementsSaved,
        });
        reloadPracticeType();
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  return (
    <>
      {selectedAktivitetskrav ? (
        <>
          {editRequirementModal}
          <ConfirmationDialog
            isOpen={showDeleteConfirmation}
            onClose={handleDeleteConfirmationClose}
            title={labels.deleteDialogTitle}
          >
            <p>{labels.deleteDialogContent}</p>
          </ConfirmationDialog>
          <PanelHeader title={selectedAktivitetskrav.navn} />
          <Button.Secondary
            className={'edit-requirement-name'}
            onClick={editRequirement}
          >
            <Icon type={IconType.Edit} />
            &nbsp;<span>{labels.editRequirementName}</span>
          </Button.Secondary>
          &nbsp;&nbsp;&nbsp;
          <Button.Secondary
            className={'delete-requirement'}
            onClick={() => setShowDeleteConfirmation(true)}
          >
            <Icon type={IconType.Delete} />
            &nbsp;<span>{labels.delete}</span>
          </Button.Secondary>
          {praksissteder
            ? praksissteder.length > 0 && (
                <PraksisstederWithAvtale
                  updatePraksisstedIdsWithKravOppfylt={
                    updatePraksisstedIdsWithKravOppfylt
                  }
                  praksissteder={praksissteder}
                />
              )
            : loading && <Spinner />}
        </>
      ) : (
        <Spinner />
      )}
    </>
  );
};

const useRequirementModal = (
  title: string,
  savebuttonText: string,
  save: (requirementName: string, close: () => void) => void,
  oldName: string = '',
) => {
  const { formatMessage } = useIntl();
  const [aktivitetskravnavn, setAktivitetskravnavn] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  useEffect(() => {
    setAktivitetskravnavn(oldName);
  }, [oldName]);

  const modalButtons = (
    <>
      <Button.Tertiary onClick={close}>
        {formatMessage({ id: translations.cancel.id })}
      </Button.Tertiary>
      <Button.Primary
        className={'save-button'}
        onClick={() => save(aktivitetskravnavn, close)}
      >
        {savebuttonText}
      </Button.Primary>
    </>
  );

  const LocalModal = (
    <Modal isOpen={isOpen} actionButtons={modalButtons} title={title}>
      <TextInput
        htmlId={'kravnavn'}
        label={formatMessage({ id: translations.requirementNameLabel.id })}
        value={aktivitetskravnavn}
        onChange={setAktivitetskravnavn}
        autoComplete="off"
      />
    </Modal>
  );
  return { open, Modal: LocalModal };
};

export default PracticeTypePage;
